import React, { useState, useEffect } from 'react';
import { Card, Col, Row, Button, Table, Badge, Form, Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { vendedorService } from '../../../services/vendedorService';

const getRoleLabel = (rol) => {
    const isAdmin = rol === 'admin' || rol === 'super_admin';
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', padding: '4px 12px',
            borderRadius: '12px', fontSize: '0.85rem', fontWeight: 500,
            backgroundColor: isAdmin ? '#FEE2E2' : '#DBEAFE',
            color: isAdmin ? '#991B1B' : '#1E40AF', width: 'fit-content'
        }}>
            {rol.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
        </span>
    );
};

const getStatusLabel = (estado) => {
    const isActivo = estado === 'activo';
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', padding: '4px 12px',
            borderRadius: '12px', fontSize: '0.85rem', fontWeight: 500,
            backgroundColor: isActivo ? '#D1FAE5' : '#FEE2E2',
            color: isActivo ? '#065F46' : '#991B1B', width: 'fit-content'
        }}>
            {estado.charAt(0).toUpperCase() + estado.slice(1)}
        </span>
    );
};

const MAX_ASESORES_POR_PROYECTO = 10;

const VendedoresConfig = () => {
    const [vendedores, setVendedores] = useState([]);
    const [loading, setLoading] = useState(true);

    // Modal Vendedor
    const [showVendedorModal, setShowVendedorModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentVendedor, setCurrentVendedor] = useState({
        nombre: '', apellido: '', email: '', password: '', rol: 'vendedor', telefono: '', estado: 'activo'
    });

    // Modal Proyectos
    const [showProyectosModal, setShowProyectosModal] = useState(false);
    const [selectedVendedor, setSelectedVendedor] = useState(null);
    const [allProyectos, setAllProyectos] = useState([]);
    const [vendedorProyectosIds, setVendedorProyectosIds] = useState([]);
    const [savingProyectos, setSavingProyectos] = useState(false);
    const [ResumenProyectos, setResumenProyectos] = useState({});

    useEffect(() => {
        fetchVendedores();
        fetchProyectos();
    }, []);

    const fetchVendedores = async () => {
        setLoading(true);
        try {
            const res = await vendedorService.getVendedores();
            if (res.success) {
                setVendedores(res.data);
            }
        } catch (error) {
            toast.error("Error al cargar la lista de vendedores");
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchProyectos = async () => {
        try {
            const res = await vendedorService.getProyectosEmpresa();
            setAllProyectos(res || []);
        } catch (error) {
            console.error("Error al cargar proyectos de la empresa:", error);
        }
    };

    // --- MANEJO DE VENDEDOR (CRUD) ---

    const handleAddVendedor = () => {
        setIsEditing(false);
        setCurrentVendedor({
            nombre: '', apellido: '', email: '', password: '', rol: 'vendedor', telefono: '', estado: 'activo'
        });
        setShowVendedorModal(true);
    };

    const handleEditVendedor = (v) => {
        setIsEditing(true);
        setCurrentVendedor({
            ...v,
            password: '' // la contraseña llega ofuscada, si es vacía no se actualiza
        });
        setShowVendedorModal(true);
    };

    const handleChangeVendedor = (e) => {
        const { name, value } = e.target;
        setCurrentVendedor(prev => ({ ...prev, [name]: value }));
    };

    const handleSaveVendedor = async () => {
        try {
            if (!currentVendedor.nombre || !currentVendedor.email) {
                toast.warning("El nombre y correo son obligatorios");
                return;
            }
            if (!isEditing && !currentVendedor.password) {
                toast.warning("La contraseña es obligatoria para nuevos usuarios");
                return;
            }

            if (isEditing) {
                await vendedorService.updateVendedor(currentVendedor.id, currentVendedor);
                toast.success("Vendedor actualizado exitosamente");
            } else {
                await vendedorService.createVendedor(currentVendedor);
                toast.success("Vendedor creado exitosamente");
            }
            setShowVendedorModal(false);
            fetchVendedores();
        } catch (error) {
            toast.error(error.response?.data?.message || "Error al procesar la solicitud");
        }
    };

    // --- MANEJO ASIGNACIÓN PROYECTOS ---

    const handleOpenProyectos = async (vendedor) => {
        setSelectedVendedor(vendedor);
        setShowProyectosModal(true);
        setSavingProyectos(true);
        try {
            const [ProyectosVendedor, ResumenAsignaciones] = await Promise.all([
                vendedorService.getProyectosPorVendedor(vendedor.id),
                ObtenerResumenProyectos(),
            ]);

            const ids = ProyectosVendedor.map(p => p.codigoProyecto);
            setVendedorProyectosIds(ids);
            setResumenProyectos(ResumenAsignaciones);
        } catch (error) {
            toast.error("Error al cargar proyectos del vendedor");
        } finally {
            setSavingProyectos(false);
        }
    };

    const ObtenerResumenProyectos = async () => {
        const ResumenActualizado = {};

        await Promise.all((allProyectos || []).map(async (ProyectoActual) => {
            const ProyectoId = ProyectoActual.codigoProyecto;

            try {
                const AsignacionesProyecto = await vendedorService.getVendedoresPorProyecto(ProyectoId);
                const VendedoresProyecto = (AsignacionesProyecto || []).map((AsignacionActual) => AsignacionActual.vendedor).filter(Boolean);
                const ActivosProyecto = VendedoresProyecto.filter((VendedorActual) => VendedorActual.estado === 'activo');

                ResumenActualizado[ProyectoId] = {
                    TotalAsignados: VendedoresProyecto.length,
                    TotalActivos: ActivosProyecto.length,
                    Vendedores: VendedoresProyecto,
                };
            } catch (ErrorActual) {
                ResumenActualizado[ProyectoId] = {
                    TotalAsignados: 0,
                    TotalActivos: 0,
                    Vendedores: [],
                    Error: true,
                };
            }
        }));

        return ResumenActualizado;
    };

    const ObtenerEstadoProyecto = (ProyectoId) => {
        const ResumenActual = ResumenProyectos[ProyectoId] || {
            TotalAsignados: 0,
            TotalActivos: 0,
            Vendedores: [],
        };

        const EstaAsignado = vendedorProyectosIds.includes(ProyectoId);
        const EstaLleno = ResumenActual.TotalAsignados >= MAX_ASESORES_POR_PROYECTO;
        const BloqueadoPorCupo = !EstaAsignado && EstaLleno;

        return {
            ...ResumenActual,
            EstaAsignado,
            EstaLleno,
            BloqueadoPorCupo,
        };
    };

    const toggleProyecto = async (proyectoId) => {
        const EstadoProyecto = ObtenerEstadoProyecto(proyectoId);
        if (EstadoProyecto.BloqueadoPorCupo) {
            toast.warning(`Este proyecto ya tiene ${MAX_ASESORES_POR_PROYECTO} asesores asignados. Desasigna uno para liberar el cupo.`);
            return;
        }

        const isAssigned = vendedorProyectosIds.includes(proyectoId);
        let newIds = [];

        if (isAssigned) {
            newIds = vendedorProyectosIds.filter(id => id !== proyectoId);
            setVendedorProyectosIds(newIds);
            try {
                const Respuesta = await vendedorService.desasignarVendedorProyecto(proyectoId, selectedVendedor.id);
                if (Respuesta?.success === false) {
                    throw new Error(Respuesta.message || "Error al quitar proyecto");
                }
                setResumenProyectos(await ObtenerResumenProyectos());
            } catch (err) {
                setVendedorProyectosIds(vendedorProyectosIds);
                toast.error(err?.response?.data?.message || err?.message || "Error al quitar proyecto");
            }
        } else {
            newIds = [...vendedorProyectosIds, proyectoId];
            setVendedorProyectosIds(newIds);
            try {
                const Respuesta = await vendedorService.asignarVendedorProyecto(proyectoId, selectedVendedor.id);
                if (Respuesta?.success === false) {
                    throw new Error(Respuesta.message || "Error al asignar proyecto");
                }
                setResumenProyectos(await ObtenerResumenProyectos());
            } catch (err) {
                setVendedorProyectosIds(vendedorProyectosIds);
                toast.error(err?.response?.data?.message || err?.message || "Error al asignar proyecto");
            }
        }
    };

    return (
        <React.Fragment>
            <div className="container-fluid">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h1 className="calendario-title">Gestión de vendedores</h1>


                </div>

                {/* Content */}
                <Row>
                    <Col xl={12}>
                        <Card className="custom-card">
                            <Card.Header className="justify-content-between">
                                <Card.Title>Vendedores Registrados</Card.Title>
                                <div className="d-flex gap-2">
                                    <Button variant="primary" size="sm" onClick={handleAddVendedor}>
                                        <i className="ri-add-line me-1 align-middle"></i> Nuevo Vendedor
                                    </Button>
                                    <Button variant="light" size="sm" onClick={fetchVendedores}>
                                        <i className="ri-refresh-line me-1 align-middle"></i> Refrescar
                                    </Button>
                                </div>
                            </Card.Header>
                            <Card.Body>
                                {loading ? (
                                    <div className="text-center p-5">
                                        <Spinner animation="border" variant="primary" />
                                        <p className="mt-2 text-muted">Cargando vendedores...</p>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <Table bordered hover className="text-nowrap">
                                            <thead>
                                                <tr>
                                                    <th>ID</th>
                                                    <th>Nombre Completo</th>
                                                    <th>Email</th>
                                                    <th>Teléfono</th>
                                                    <th>Rol</th>
                                                    <th>Estado</th>
                                                    <th className="text-center">Acciones</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {vendedores.length === 0 ? (
                                                    <tr><td colSpan="7" className="text-center p-4">No hay vendedores registrados</td></tr>
                                                ) : vendedores.map((v) => (
                                                    <tr key={v.id}>
                                                        <td>{v.id}</td>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <span className="avatar avatar-sm avatar-rounded bg-primary-transparent text-primary me-2">
                                                                    {v.nombre?.charAt(0)}{v.apellido?.charAt(0) || ''}
                                                                </span>
                                                                <span className="fw-semibold">{v.nombre} {v.apellido}</span>
                                                            </div>
                                                        </td>
                                                        <td>{v.email}</td>
                                                        <td>{v.telefono || '-'}</td>
                                                        <td>
                                                            {getRoleLabel(v.rol)}
                                                        </td>
                                                        <td>
                                                            {getStatusLabel(v.estado)}
                                                        </td>
                                                        <td className="text-center">
                                                            <Button variant="info-light" size="sm" className="me-2" onClick={() => handleEditVendedor(v)}>
                                                                <i className="ri-edit-line"></i> Editar
                                                            </Button>
                                                            {v.rol !== 'admin' && v.rol !== 'super_admin' ? (
                                                                <Button variant="secondary-light" size="sm" onClick={() => handleOpenProyectos(v)}>
                                                                    <i className="ri-folder-shared-line"></i> Proyectos
                                                                </Button>
                                                            ) : (
                                                                <span className="text-muted small ms-2" title="Los administradores tienen acceso a todos los proyectos por defecto"><i className="ri-shield-star-line text-warning"></i> Todo acceso</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </div>

            {/* Modal CRUD Vendedor */}
            <Modal show={showVendedorModal} onHide={() => setShowVendedorModal(false)} backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>{isEditing ? 'Editar Vendedor' : 'Nuevo Vendedor'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Nombre <span className="text-danger">*</span></Form.Label>
                                    <Form.Control type="text" name="nombre" value={currentVendedor.nombre} onChange={handleChangeVendedor} placeholder="Ej. Juan" />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Apellido</Form.Label>
                                    <Form.Control type="text" name="apellido" value={currentVendedor.apellido || ''} onChange={handleChangeVendedor} placeholder="Ej. Pérez" />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label>Correo Electrónico <span className="text-danger">*</span></Form.Label>
                                    <Form.Control type="email" name="email" value={currentVendedor.email} onChange={handleChangeVendedor} placeholder="correo@ejemplo.com" />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label>Contraseña {isEditing && <span className="text-muted fw-normal">(opcional, llenar para cambiar)</span>}</Form.Label>
                                    <Form.Control type="password" name="password" value={currentVendedor.password} onChange={handleChangeVendedor} placeholder="***" />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Teléfono</Form.Label>
                                    <Form.Control type="text" name="telefono" value={currentVendedor.telefono || ''} onChange={handleChangeVendedor} placeholder="+51..." />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Rol</Form.Label>
                                    <Form.Select name="rol" value={currentVendedor.rol} onChange={handleChangeVendedor}>
                                        <option value="vendedor">Vendedor</option>
                                        <option value="admin">Administrador</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Estado</Form.Label>
                                    <Form.Select name="estado" value={currentVendedor.estado} onChange={handleChangeVendedor}>
                                        <option value="activo">Activo</option>
                                        <option value="inactivo">Inactivo</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={() => setShowVendedorModal(false)}>
                        Cancelar
                    </Button>
                    <Button variant="primary" onClick={handleSaveVendedor}>
                        {isEditing ? 'Guardar Cambios' : 'Crear Vendedor'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal Asignación Proyectos */}
            <Modal show={showProyectosModal} onHide={() => setShowProyectosModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Asignar proyectos a vendedor</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedVendedor && (
                        <div className="mb-4 d-flex align-items-center bg-light p-3 rounded border">
                            <div className="avatar avatar-md avatar-rounded bg-primary text-fixed-white me-3">
                                {selectedVendedor.nombre?.charAt(0)}
                            </div>
                            <div>
                                <h6 className="mb-0 fw-semibold">{selectedVendedor.nombre} {selectedVendedor.apellido}</h6>
                                <span className="text-muted fs-12">{selectedVendedor.email}</span>
                            </div>
                        </div>
                    )}

                    {selectedVendedor?.estado === 'inactivo' && (
                        <div className="alert alert-warning border-0 mb-4" style={{ backgroundColor: '#FFF7E6', color: '#8A5A00' }}>
                            <div className="fw-semibold mb-1">
                                Este asesor está inactivo
                            </div>
                            <div className="small">
                                Puede conservar sus proyectos asignados, pero no recibirá notificaciones ni nuevas atenciones directas mientras siga inactivo. El sistema escalará los leads a otros responsables activos del proyecto.
                            </div>
                        </div>
                    )}

                    <p className="text-muted fw-semibold mb-3">Seleccione los proyectos a los que este vendedor tendrá acceso en el Dashboard y Base de Datos:</p>

                    <div className="d-flex flex-wrap gap-2 mb-3">
                        <Badge bg="light" text="dark" pill className="border">
                            Máximo {MAX_ASESORES_POR_PROYECTO} asesores por proyecto
                        </Badge>
                        <Badge bg="light" text="dark" pill className="border">
                            Inactivos sin notificaciones
                        </Badge>
                    </div>

                    {savingProyectos ? (
                        <div className="text-center p-4"><Spinner animation="border" size="sm" className="me-2" /> Cargando permisos...</div>
                    ) : (
                        <div className="row g-3">
                            {allProyectos.map(proyecto => {
                                const EstadoProyecto = ObtenerEstadoProyecto(proyecto.codigoProyecto);
                                const isChecked = EstadoProyecto.EstaAsignado;
                                const CardBorder = EstadoProyecto.BloqueadoPorCupo
                                    ? 'border-danger bg-danger-transparent'
                                    : isChecked
                                        ? 'border-primary bg-primary-transparent'
                                        : 'border-dashed';

                                return (
                                    <div className="col-12 col-md-6" key={proyecto.codigoProyecto}>
                                        <div
                                            className={`card border mb-0 ${CardBorder}`}
                                            onClick={() => !EstadoProyecto.BloqueadoPorCupo && toggleProyecto(proyecto.codigoProyecto)}
                                            style={{
                                                transition: 'all 0.2s',
                                                cursor: EstadoProyecto.BloqueadoPorCupo ? 'not-allowed' : 'pointer',
                                                opacity: EstadoProyecto.BloqueadoPorCupo ? 0.78 : 1,
                                            }}
                                        >
                                            <div className="card-body p-3 d-flex align-items-center justify-content-between">
                                                <div className="d-flex align-items-center">
                                                    <div className="me-3">
                                                        <Form.Check
                                                            type="switch"
                                                            id={`switch-proy-${proyecto.codigoProyecto}`}
                                                            checked={isChecked}
                                                            disabled={EstadoProyecto.BloqueadoPorCupo}
                                                            readOnly
                                                            className="form-switch-lg"
                                                        />
                                                    </div>
                                                    <div>
                                                        <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                                                            <span className={`fw-semibold d-block ${isChecked ? 'text-primary' : EstadoProyecto.BloqueadoPorCupo ? 'text-danger' : ''}`}>
                                                                {proyecto.nombreProyecto}
                                                            </span>
                                                            <Badge bg={EstadoProyecto.BloqueadoPorCupo ? 'danger' : EstadoProyecto.EstaLleno ? 'warning' : 'light'} text={EstadoProyecto.BloqueadoPorCupo ? 'white' : 'dark'} pill>
                                                                {EstadoProyecto.TotalAsignados}/{MAX_ASESORES_POR_PROYECTO} asignados
                                                            </Badge>
                                                            <Badge bg="success" pill>
                                                                {EstadoProyecto.TotalActivos} activos
                                                            </Badge>
                                                        </div>
                                                        <span className="text-muted fs-12 d-block">
                                                            {EstadoProyecto.BloqueadoPorCupo
                                                                ? 'Cupo completo. Desasigna un asesor para liberar espacio.'
                                                                : `Click para ${isChecked ? 'quitar' : 'asignar'} acceso`}
                                                        </span>
                                                        {EstadoProyecto.Vendedores?.length > 0 && (
                                                            <span className="text-muted fs-11 d-block mt-1">
                                                                {EstadoProyecto.Vendedores.map((VendedorActual) => `${VendedorActual.nombre} ${VendedorActual.apellido || ''}`.trim()).join(', ')}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <i className={`ri-shield-check-fill fs-24 ${isChecked ? 'text-primary' : EstadoProyecto.BloqueadoPorCupo ? 'text-danger' : 'text-muted opacity-25'}`}></i>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}

                            {allProyectos.length === 0 && (
                                <div className="col-12 text-center p-4 border border-dashed rounded text-muted">
                                    No hay proyectos registrados en el sistema.
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowProyectosModal(false)}>
                        <i className="ri-check-line me-1"></i> Listo
                    </Button>
                </Modal.Footer>
            </Modal>
        </React.Fragment>
    );
};

export default VendedoresConfig;
